import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Keyboard, Platform, Pressable, useWindowDimensions, View } from "react-native";
import { parse, isValid, startOfDay } from "date-fns";
import { useUnistyles } from "react-native-unistyles";
import "../../theme";
import { Icon } from "../Icon";
import { Input } from "../Input";
import { Calendar } from "./Calendar";
import { Popover } from "./Popover";
import { usePopover } from "./usePopover";
import { formatDisplayDate, isDateDisabled, maskDateInput, type WeekStartsOn } from "./date-utils";

export interface SingleDatePickerProps {
  /** Currently selected date (controlled). `null` means nothing selected. */
  readonly value: Date | null;
  /** Called when the selected date changes (or is cleared → `null`). */
  readonly onChange: (date: Date | null) => void;
  /** Floating field label. */
  readonly label?: string | undefined;
  /** Placeholder shown when no date is selected. */
  readonly placeholder?: string | undefined;
  /** Earliest selectable date (inclusive). */
  readonly minDate?: Date | undefined;
  /** Latest selectable date (inclusive). */
  readonly maxDate?: Date | undefined;
  /** Disables the field. */
  readonly disabled?: boolean | undefined;
  /** Error message; turns the border red and shows below the field. */
  readonly error?: string | undefined;
  /** Neutral helper text below the field. */
  readonly helperText?: string | undefined;
  /** Shows a clear button when a date is selected. */
  readonly clearable?: boolean | undefined;
  /** `date-fns` format for the displayed value. Defaults to `d MMM yyyy`. */
  readonly format?: string | undefined;
  /** First day of the week (0 = Sunday … 6 = Saturday). Defaults to Monday. */
  readonly weekStartsOn?: WeekStartsOn | undefined;
  /** Reference "today". Defaults to the current day. */
  readonly today?: Date | undefined;
  /** Parse typed text into a Date. Defaults to `dd/MM/yyyy` parsing. */
  readonly parseInput?: ((text: string) => Date | null) | undefined;
  /** Format a Date for the editable input field. Defaults to `dd/MM/yyyy`. */
  readonly formatInput?: ((date: Date) => string) | undefined;
  /** Mask raw keyboard input (auto-insert separators). Return the masked string. */
  readonly maskInput?: ((raw: string, previous: string) => string) | undefined;
  /** Placeholder shown while the input is focused/editable. Defaults to `DD/MM/YYYY`. */
  readonly inputPlaceholder?: string | undefined;
  /**
   * Called when the calendar popover opens or closes. Forms that stack several
   * pickers need this: the popover can flip above its field when there is no
   * room below, and only the form knows which wrapper must out-rank the others
   * for the flipped calendar to stay on top.
   */
  readonly onOpenChange?: ((open: boolean) => void) | undefined;
}

const INPUT_FORMAT = "dd/MM/yyyy";
const INPUT_PLACEHOLDER = "DD/MM/YYYY";

function parseInputDate(text: string): Date | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const parsed = parse(trimmed, INPUT_FORMAT, new Date());
  return isValid(parsed) ? startOfDay(parsed) : null;
}

function defaultFormatInput(d: Date): string {
  return formatDisplayDate(d, INPUT_FORMAT);
}

/**
 * Single date picker — an editable {@link Input} that accepts typed dates
 * (DD/MM/YYYY) or opens a calendar popover via the icon button. Renders
 * identically on web and native (built from RN primitives).
 */
const SingleDatePicker: FC<SingleDatePickerProps> = ({
  value,
  onChange,
  label,
  placeholder = "Select date",
  minDate,
  maxDate,
  disabled = false,
  error,
  helperText,
  clearable = false,
  format = "dd/MM/yyyy",
  weekStartsOn = 1,
  today: todayProp,
  parseInput = parseInputDate,
  formatInput = defaultFormatInput,
  maskInput = maskDateInput,
  inputPlaceholder = INPUT_PLACEHOLDER,
  onOpenChange,
}) => {
  const { theme } = useUnistyles();
  const { isOpen, toggle, close, open, containerRef } = usePopover();
  const today = useMemo(() => startOfDay(todayProp ?? new Date()), [todayProp]);

  const displayValue = value ? formatDisplayDate(value, format) : "";
  const editableValue = value ? formatInput(value) : "";

  const [inputText, setInputText] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const committedRef = useRef(false);
  const inputTextRef = useRef(inputText);
  inputTextRef.current = inputText;

  useEffect(() => {
    if (!isFocused) {
      setInputText(editableValue);
    }
  }, [editableValue, isFocused]);

  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    committedRef.current = false;
    setInputText(editableValue);
    // On native the soft keyboard would overlap the calendar, so only
    // auto-open the popover on web. Native users open via the icon button.
    if (Platform.OS === "web") {
      open();
    } else {
      close();
    }
  }, [editableValue, open, close]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    if (committedRef.current) return;

    const trimmed = inputTextRef.current.trim();
    if (!trimmed) {
      onChange(null);
      return;
    }

    const parsed = parseInput(trimmed);
    if (parsed && !isDateDisabled(parsed, minDate, maxDate)) {
      onChange(parsed);
      return;
    }
    setInputText(editableValue);

    // Close popover after a tick so calendar press events can fire first.
    requestAnimationFrame(() => {
      if (!committedRef.current) close();
    });
  }, [editableValue, onChange, minDate, maxDate, close, parseInput]);

  const handleSelectDate = useCallback(
    (date: Date) => {
      if (isDateDisabled(date, minDate, maxDate)) return;
      committedRef.current = true;
      onChange(startOfDay(date));
      close();
    },
    [onChange, close, minDate, maxDate],
  );

  const handleClear = useCallback(() => {
    setInputText("");
    onChange(null);
  }, [onChange]);

  const handleChangeText = useCallback(
    (text: string) => {
      setInputText(maskInput(text, inputTextRef.current));
    },
    [maskInput],
  );

  const handleToggleCalendar = useCallback(() => {
    if (Platform.OS !== "web") {
      Keyboard.dismiss();
    }
    toggle();
  }, [toggle]);

  const anchor = (
    <Input
      value={isFocused ? inputText : displayValue}
      onChangeText={handleChangeText}
      placeholder={isFocused ? inputPlaceholder : placeholder}
      label={label}
      error={error}
      helperText={helperText}
      disabled={disabled}
      active={isOpen}
      clearable={clearable}
      onClear={handleClear}
      onFocus={handleFocus}
      onBlur={handleBlur}
      accessibilityLabel={label ?? placeholder}
      rightContent={
        <Pressable onPress={handleToggleCalendar} hitSlop={8} accessibilityLabel="Open calendar">
          <Icon name="calendarOutline" size={18} color={theme.colors.textMuted} />
        </Pressable>
      }
    />
  );

  return (
    <Popover isOpen={isOpen} onClose={close} containerRef={containerRef} anchor={anchor}>
      <CalendarBody
        key={isOpen ? "open" : "closed"}
        today={today}
        defaultMonth={value ?? today}
        selected={value}
        weekStartsOn={weekStartsOn}
        minDate={minDate}
        maxDate={maxDate}
        onSelectDate={handleSelectDate}
      />
    </Popover>
  );
};

interface CalendarBodyProps {
  readonly today: Date;
  readonly defaultMonth: Date;
  readonly selected: Date | null;
  readonly weekStartsOn: WeekStartsOn;
  readonly minDate?: Date | undefined;
  readonly maxDate?: Date | undefined;
  readonly onSelectDate: (date: Date) => void;
}

const CalendarBody: FC<CalendarBodyProps> = ({
  today,
  defaultMonth,
  selected,
  weekStartsOn,
  minDate,
  maxDate,
  onSelectDate,
}) => {
  const { width: windowWidth } = useWindowDimensions();
  const dropdownPadding = 32;
  const pageMargin = 32;
  const availableWidth = windowWidth - dropdownPadding - pageMargin;
  const cellSize = availableWidth < 320 ? Math.max(28, Math.floor(availableWidth / 7)) : undefined;

  return (
    <View>
      <Calendar
        weekStartsOn={weekStartsOn}
        today={today}
        defaultMonth={defaultMonth}
        selectedStart={selected}
        selectedEnd={null}
        hoveredDate={null}
        rangeMode={false}
        minDate={minDate}
        maxDate={maxDate}
        onSelectDate={onSelectDate}
        cellSize={cellSize}
      />
    </View>
  );
};

CalendarBody.displayName = "SingleCalendarBody";
SingleDatePicker.displayName = "SingleDatePicker";

export { SingleDatePicker };
